import { JsonProperty } from "../utilities/json-property";
import { OrderHistoryEventType } from "./enums/order-history-event-type";
import { OrderInvoiceStatus } from "./enums/order-invoice-status";
import { OrderProductionStatus } from "./enums/order-production-status";
import { OrderShipmentStatus } from "./enums/order-shipment-status";
import { OrderStatus } from "./enums/order-status";
import { PendingReason } from "./enums/pending-reason"; 

export const orderHistoryEventMapper = (type : any) => {

    switch (type) {
        case 0: 
            return OrderCreatedHistoryEvent;
        case 1:
            return OrderUpdatedHistoryEvent;
        case 2:
            return OrderStatusUpdatedHistoryEvent;
        case 3:
            return OrderCancelledHistoryEvent;
        case 4: 
            return OrderAddedToShipmentHistoryEvent;
        case 5: 
            return OrderInvoicedHistoryEvent;
        case 6:
            return OrderApprovedHistoryEvent;
        case 7:
            return OrderStatusUpdatedV2HistoryEvent;
        default:
            return {};
    }

};

export class OrderHistory {
    
    public id : string = '';
    
    @JsonProperty({ enumMapper: orderHistoryEventMapper, enumProp: '$type', designType: 'array' })
    public events : OrderHistoryEvent[] = [];    
}

export class OrderHistoryEvent {
    
    public id : string = '';

    @JsonProperty({ toType: 'datetime' })
    public timestamp : Date = new Date();

    public $type : OrderHistoryEventType|undefined = undefined;

}

export class OrderCreatedHistoryEvent extends OrderHistoryEvent {

    public order : string = '';

    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderCreated;
    }
}

export class OrderUpdatedHistoryEvent extends OrderHistoryEvent {

    public order : string = '';
    
    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderUpdated;
    }
}

/**
 * The statuses an order had before they were reworked, which are the numbers that the status updates of the
 * first kind (type 2) were stored with.  They are not the statuses of an order any more, so they are only for
 * naming those events.
 */
export const LegacyOrderStatusMetadata : { [status: string]: { display: string } } = {
    '-1': { display: 'Cancelled' },
    '0': { display: 'Pending' },
    '1': { display: 'Open' },
    '2': { display: 'Planned' },
    '3': { display: 'Routed' },
    '4': { display: 'In-Production' },
    '5': { display: 'Produced' },
    '6': { display: 'Loaded' },
    '7': { display: 'In-Transit' },
    '8': { display: 'Delivered' },
    '9': { display: 'Invoiced' }
};

export class OrderStatusUpdatedHistoryEvent extends OrderHistoryEvent {
    // One of the legacy statuses, not an OrderStatus
    public status : number = OrderStatus.Open;

    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderStatusUpdated;
    }
}

/**
 * The status update the API writes now, which has every status of the order and not only the one
 */
export class OrderStatusUpdatedV2HistoryEvent extends OrderHistoryEvent {
    public status : OrderStatus = OrderStatus.Open;
    public shipmentStatus : OrderShipmentStatus = OrderShipmentStatus.NotApplicable;
    public productionStatus : OrderProductionStatus = OrderProductionStatus.NotApplicable;
    public invoiceStatus : OrderInvoiceStatus = OrderInvoiceStatus.NotApplicable;
    public pendingReason : PendingReason = PendingReason.None;

    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderStatusUpdatedV2;
    }
}

export class OrderCancelledHistoryEvent extends OrderHistoryEvent {
    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderCancelled;
    }
}

export class OrderAddedToShipmentHistoryEvent extends OrderHistoryEvent {

    public shipmentId : number|undefined = undefined;
    public deliveryId : number|undefined = undefined;

    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderAddedToShipment;
    }
}

export class OrderInvoicedHistoryEvent extends OrderHistoryEvent {

    public invoiceId : number = 0;
    public invoiceNumber : string = '';

    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderInvoiced;
    }
}

export class OrderApprovedHistoryEvent extends OrderHistoryEvent {

    @JsonProperty({ toType: 'datetime' })
    public approvalDate : Date = new Date();
    public approvalAmount : number = 0;
    public approvingUser: string = '';
    public reason: PendingReason = PendingReason.None;

    constructor() {
        super();
        this.$type = OrderHistoryEventType.OrderApproved;
    }
}
